/**
 * MUI EntryInput Component
 * 
 * This component is the Material UI equivalent of the original EntryInput component.
 * It provides text and number input fields with labels.
 */

import React, { useState, useEffect, ChangeEvent } from 'react';
import { Box, Stack, IconButton, InputAdornment } from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useDispatch, useSelector } from 'react-redux';
import { MuiInput as Input } from '../atoms';
import InputLabel from '../atoms/InputLabel';
import ReducersIndex from 'reduxModules/ducks';
import useDebounce from 'hooks/useDebounce';
import { getCurrentValue2 } from 'reduxModules/selectors/containerSelectors';
import { isInError } from 'reduxModules/ducks/ui';
import { isObject, parseInt, clamp, get } from 'lodash';
import { 
  EntryProps, 
  SingleEntryProps, 
  TextInputProps, 
  NumberInputProps, 
  DoubleNumberInputProps 
} from 'types/components/molecules';
import { AnyAction } from '@reduxjs/toolkit';

/**
 * MUI Entry component
 * 
 * @param props - Component props
 * @returns The MUI Entry component
 */
const MuiEntry: React.FC<EntryProps> = (props) => {
  // Helper function to safely call getCurrentValue2 with TypeScript
  const safeGetValue = (state: any, param?: string) => {
    if (!param) return null;
    // Cast the function to allow any number of arguments
    const getValue = getCurrentValue2 as any;
    return getValue(state, param);
  };

  let currentValue = useSelector((state: any) => 
    props.dispatcher ? safeGetValue(state, props.dispatcher) : null
  );
  
  // Cast isInError to any to avoid TypeScript errors
  const isInErrorAny = isInError as any;
  const inError = useSelector((state: any) => 
    isInErrorAny(state, props)
  ) as boolean;
  
  const { min = -Infinity, max = +Infinity } = props?.range || {};
  
  currentValue = isObject(currentValue) && props.name 
    ? get(currentValue, props.name) 
    : currentValue;
    
  const [value, setValue] = useState<string | number>(currentValue || '');
  const dispatch = useDispatch();

  useEffect(() => {
    // Enhanced validation to prevent corrupted values
    const validatedValue = currentValue || '';
    setValue(validatedValue);
  }, [currentValue]);

  const debounceDispatch = useDebounce(() => {
    if (props.dispatcher) {
      let dispatchValue: string | number | Record<string, string | number> = value;
      
      if (props.name) {
        dispatchValue = { ...{}, [props.name]: value };
      } else if (props.isNumber && typeof value === 'string') {
        // Enhanced parsing with proper validation
        const parsedValue = parseInt(value, 10);
        dispatchValue = isNaN(parsedValue) ? 0 : clamp(parsedValue, min, max);
      }
      
      dispatch(
        ReducersIndex.container[props.dispatcher](dispatchValue) as unknown as AnyAction
      );
    }
  }); // The useDebounce hook uses a hardcoded 500ms delay

  /**
   * Handle change event with improved validation
   * 
   * @param evt - The change event
   */
  const handleChange = (evt: ChangeEvent<HTMLInputElement>): void => {
    const newValue = evt.target.value;
    
    if (props.isNumber) {
      // For number inputs, validate and sanitize the input
      if (newValue === '') {
        setValue('');
      } else {
        const parsedValue = parseInt(newValue, 10);
        if (!isNaN(parsedValue)) {
          setValue(clamp(parsedValue, min, max));
        }
        // If parsing fails, don't update the value to prevent corruption
      }
    } else {
      setValue(newValue);
    }
    
    debounceDispatch();
  };

  /**
   * Handle increment button click
   */
  const handleIncrement = () => {
    if (!props.isNumber) return;
    
    const currentNumValue = typeof value === 'number' ? value : parseInt(String(value), 10) || 0;
    const newValue = clamp(currentNumValue + 1, min, max);
    setValue(newValue);
    debounceDispatch();
  };

  /**
   * Handle decrement button click
   */
  const handleDecrement = () => {
    if (!props.isNumber) return;
    
    const currentNumValue = typeof value === 'number' ? value : parseInt(String(value), 10) || 0;
    const newValue = clamp(currentNumValue - 1, min, max);
    setValue(newValue);
    debounceDispatch();
  };

  return (
    <Input 
      name={props.name} 
      type="text" // Always use text to avoid browser's native number arrows
      onChange={handleChange} 
      value={value} 
      isNav={props.isNav} 
      isEntry={props.isEntry ?? true} 
      error={inError}
      readOnly={props.readOnly}
      inputProps={props.isNumber ? {
        inputMode: 'numeric', // This provides a numeric keyboard on mobile
        pattern: '[0-9]*'      // This helps with validation
      } : undefined}
      InputProps={props.isNumber ? {
        endAdornment: (
          <InputAdornment position="end">
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              marginRight: '2px',
              height: '28px',
              justifyContent: 'space-between',
              opacity: 0,
              transition: 'opacity 0.2s ease',
              '.MuiInputBase-root:hover &': {
                opacity: 1,
              },
            }}>
              <IconButton
                size="small"
                onClick={handleIncrement}
                tabIndex={-1}
                sx={{ 
                  padding: '0px',
                  minWidth: '16px',
                  width: '16px',
                  height: '14px',
                  borderRadius: '2px',
                  '& svg': {
                    fontSize: '18px',
                    color: 'text.secondary',
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                <ArrowDropUpIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleDecrement}
                tabIndex={-1}
                sx={{ 
                  padding: '0px',
                  minWidth: '16px',
                  width: '16px',
                  height: '14px',
                  borderRadius: '2px',
                  '& svg': {
                    fontSize: '18px',
                    color: 'text.secondary',
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                <ArrowDropDownIcon />
              </IconButton>
            </Box>
          </InputAdornment>
        ),
      } : undefined}
      sx={{
        '& .MuiOutlinedInput-root': {
          paddingRight: props.isNumber ? '4px' : undefined,
        },
      }}
    />
  );
};

/**
 * MUI SingleEntry component
 * 
 * @param props - Component props
 * @returns The MUI SingleEntry component
 */
const MuiSingleEntry: React.FC<SingleEntryProps> = (props) => {
  return (
    <Box 
      sx={{
        display: 'flex',
        flexDirection: props.isNav ? 'column' : 'row',
        alignItems: props.isNav ? 'stretch' : 'center',
        gap: 2,
        marginBottom: 2,
        width: '100%'
      }}
      className={props.className || ''}
    >
      {props.label && (
        <Box sx={{ minWidth: '120px', flexShrink: 0 }}>
          <InputLabel>
            {props.label}
          </InputLabel>
        </Box>
      )}
      <Box sx={{ flex: 1 }}>
        <MuiEntry {...props} />
      </Box>
    </Box>
  );
};

/**
 * MUI TextInput component
 * 
 * @param props - Component props
 * @returns The MUI TextInput component
 */
const MuiTextInput: React.FC<TextInputProps> = (props) => 
  <MuiSingleEntry {...props} />;

/**
 * MUI NumberInput component
 * 
 * @param props - Component props
 * @returns The MUI NumberInput component
 */
const MuiNumberInput: React.FC<NumberInputProps> = (props) => 
  <MuiSingleEntry {...props} isNumber={true} />;

/**
 * MUI DoubleNumberInput component
 * 
 * @param props - Component props
 * @returns The MUI DoubleNumberInput component
 */
const MuiDoubleNumberInput: React.FC<DoubleNumberInputProps> = (props) => {
  const [first, second] = props?.ids ?? [];
  return (
    <Box className={`d-flex flex-wrap gap-1 ${props.className || ''}`}>
      {props.label && (
        <InputLabel className={`${props.isNav ? 'w-100' : ''}`}>
          {props.label}
        </InputLabel>
      )}
      <Stack direction="row" spacing="5px">
        <MuiEntry name={first} {...props} isNumber={true} isNav={false} />
        {second && <MuiEntry name={second} {...props} isNumber={true} isNav={false} />}
      </Stack>
    </Box>
  );
};

export type { 
  EntryProps, 
  SingleEntryProps, 
  TextInputProps, 
  NumberInputProps, 
  DoubleNumberInputProps 
};
export { MuiTextInput, MuiNumberInput, MuiDoubleNumberInput };
